import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import type {
  InterpreterCatalogDoc,
  InterpreterCategory,
  InterpreterStatus,
  InterpreterSuggestion,
} from './types';

export function useInterpreterStatus() {
  const { token } = useAuthStore();
  return useQuery<InterpreterStatus>({
    queryKey: ['interpreter', 'status'],
    queryFn: () => cpFetch('/iot/interpreter/status'),
    enabled: !!token,
    staleTime: 15_000,
  });
}

export function useInterpreterMappings(type: 'unspsc' | 'iso11783_units') {
  const { token } = useAuthStore();
  return useQuery<InterpreterCatalogDoc | null>({
    queryKey: ['interpreter', 'mappings', type],
    queryFn: async () => {
      const response = await cpFetch(`/iot/interpreter/mappings?type=${encodeURIComponent(type)}`);
      return response?.mapping || null;
    },
    enabled: !!token,
    staleTime: 15_000,
  });
}

export function useRefreshInterpreterMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => cpFetch('/iot/interpreter/mappings/refresh', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interpreter'] });
    },
  });
}

export function useUpsertInterpreterMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { type: 'unspsc' | 'iso11783_units'; key: string; payload: Record<string, any> }) =>
      cpFetch(`/iot/interpreter/mappings/${encodeURIComponent(input.type)}/${encodeURIComponent(input.key)}`, {
        method: 'PUT',
        body: JSON.stringify(input.payload || {}),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['interpreter', 'mappings', variables.type] });
      qc.invalidateQueries({ queryKey: ['interpreter', 'status'] });
    },
  });
}

export function useInterpreterSuggestions(status = 'pending') {
  const { token } = useAuthStore();
  return useQuery<InterpreterSuggestion[]>({
    queryKey: ['interpreter', 'suggestions', status],
    queryFn: async () => {
      const response = await cpFetch(`/iot/interpreter/suggestions?status=${encodeURIComponent(status)}`);
      return response?.suggestions || [];
    },
    enabled: !!token,
    staleTime: 10_000,
  });
}

export function useApproveInterpreterSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) =>
      cpFetch(`/iot/interpreter/suggestions/${encodeURIComponent(suggestionId)}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interpreter', 'suggestions'] });
      qc.invalidateQueries({ queryKey: ['interpreter', 'status'] });
      qc.invalidateQueries({ queryKey: ['interpreter', 'mappings'] });
    },
  });
}

export function useRejectInterpreterSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { suggestionId: string; reason?: string }) =>
      cpFetch(`/iot/interpreter/suggestions/${encodeURIComponent(input.suggestionId)}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: input.reason || '' }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interpreter', 'suggestions'] });
      qc.invalidateQueries({ queryKey: ['interpreter', 'status'] });
    },
  });
}

export function useInterpreterCategories() {
  const { token } = useAuthStore();
  return useQuery<InterpreterCategory[]>({
    queryKey: ['interpreter', 'categories'],
    queryFn: async () => {
      const response = await cpFetch('/iot/interpreter/categories');
      return response?.categories || [];
    },
    enabled: !!token,
    staleTime: 15_000,
  });
}

export function useUpsertInterpreterCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (category: Partial<InterpreterCategory> & { id: string }) =>
      cpFetch(`/iot/interpreter/categories/${encodeURIComponent(category.id)}`, {
        method: 'PUT',
        body: JSON.stringify(category),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interpreter', 'categories'] });
      qc.invalidateQueries({ queryKey: ['interpreter', 'status'] });
    },
  });
}

export function useReplayInterpreterPayload() {
  return useMutation({
    mutationFn: async (input: { payload: Record<string, any>; topic?: string }) =>
      cpFetch('/iot/interpreter/replay', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}
